import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from '../../utils/axiosConfig';

const TestimonialManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [testimonials, setTestimonials] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    level: '',
    testimonial: '',
    isActive: true
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get('/api/v2/testimonials/admin');
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      enqueueSnackbar('Error fetching testimonials', { variant: 'error' });
    }
  };

  const handleOpenDialog = (testimonial = null) => {
    if (testimonial) {
      setEditingTestimonial(testimonial);
      setFormData({
        name: testimonial.name,
        level: testimonial.level,
        testimonial: testimonial.testimonial,
        isActive: testimonial.isActive
      });
    } else {
      setEditingTestimonial(null);
      setFormData({
        name: '',
        level: '',
        testimonial: '',
        isActive: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTestimonial(null);
    setFormData({
      name: '',
      level: '',
      testimonial: '',
      isActive: true
    });
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'isActive' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTestimonial) {
        await axios.patch(`/api/v2/testimonials/${editingTestimonial._id}`, formData);
        enqueueSnackbar('Testimonial updated successfully', { variant: 'success' });
      } else {
        await axios.post('/api/v2/testimonials', formData);
        enqueueSnackbar('Testimonial created successfully', { variant: 'success' });
      }
      handleCloseDialog();
      fetchTestimonials();
    } catch (error) {
      console.error('Error saving testimonial:', error);
      enqueueSnackbar(error.response?.data?.message || 'Error saving testimonial', { variant: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this testimonial?')) {
      try {
        await axios.delete(`/api/v2/testimonials/${id}`);
        enqueueSnackbar('Testimonial deleted successfully', { variant: 'success' });
        fetchTestimonials();
      } catch (error) {
        console.error('Error deleting testimonial:', error);
        enqueueSnackbar('Error deleting testimonial', { variant: 'error' });
      }
    }
  };

  const renderTestimonialCards = () => {
    return (
      <Grid container spacing={3}>
        {testimonials.map((testimonial) => (
          <Grid item xs={12} md={4} key={testimonial._id}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative'
              }}
            >
              <CardContent>
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                  <IconButton 
                    size="small" 
                    onClick={() => handleOpenDialog(testimonial)}
                    sx={{ mr: 1 }}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => handleDelete(testimonial._id)}
                  >
                    <Delete />
                  </IconButton>
                </Box>
                <Typography variant="h6" gutterBottom>
                  {testimonial.name}
                </Typography>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  {testimonial.level}
                </Typography>
                <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
                  "{testimonial.testimonial}"
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={testimonial.isActive}
                        onChange={async (e) => {
                          try {
                            await axios.patch(`/api/v2/testimonials/${testimonial._id}`, {
                              isActive: e.target.checked
                            });
                            fetchTestimonials();
                          } catch (error) {
                            enqueueSnackbar('Error updating status', { variant: 'error' });
                          }
                        }}
                      />
                    }
                    label="Active"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Athlete Testimonials
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Testimonial
        </Button>
      </Box>

      {renderTestimonialCards()}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTestimonial ? 'Edit Testimonial' : 'Add New Testimonial'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Athlete Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Level</InputLabel>
              <Select
                name="level"
                value={formData.level}
                onChange={handleChange}
                required
                label="Level"
              >
                <MenuItem value="High School Athlete">High School Athlete</MenuItem>
                <MenuItem value="College Player">College Player</MenuItem>
                <MenuItem value="Youth Player">Youth Player</MenuItem>
                <MenuItem value="8th Grade">8th Grade</MenuItem>
                <MenuItem value="7th Grade">7th Grade</MenuItem>
                <MenuItem value="6th Grade">6th Grade</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Testimonial"
              name="testimonial"
              value={formData.testimonial}
              onChange={handleChange}
              required
              multiline
              rows={4}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={handleChange}
                  name="isActive"
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingTestimonial ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TestimonialManagement;
